import { CabIcon } from "@CabComponents/CabIcon";
import CabSpinner from "@CabComponents/CabSpinner";
import { Popover, Box, Typography, IconButton, AvatarGroup } from "@mui/material";
import { ReactElement, useState } from "react";
import { Calendar, UICalendarEventConsolidated } from "../../../../store";
import colors from "../../../../colors";
import CabAvatar from "@CabComponents/CabAvatar";
import { CabButton } from "@CabComponents/CabButton";
import { 
  IoCheckmarkCircle, IoCloseCircle, IoHelpCircle, IoInformationCircle, IoClose, IoSquare, IoCalendarClearOutline, 
  IoPeopleOutline, IoBriefcaseOutline, IoTimerOutline, IoRepeat, IoLocationOutline, IoDocumentTextOutline, 
  IoCreateOutline, IoRefreshOutline
} from "react-icons/io5";
import EditEventModal from "./EditEventModal";
import { EventAttendee } from "../../../../store/cabinetApi/generated/scheduling";
import { DateTime } from "luxon";
import { getLocalTimeZoneName } from "../../../../utils/scheduleUtils";
import { formatSlotTimeTiny } from "../../../../utils/datesUtils";


export interface Props {
  open: boolean;
  onClosePopover: () => void;
  popoverAnchorEl: Element | null;
  event: UICalendarEventConsolidated;
  eventLoading: boolean;
  calendarInfo?: Calendar[];
  // Brief-related props
  briefLoading: boolean;
  shouldShowPrepareBrief: boolean;
  shouldShowShowBrief: boolean;
  // Feature flags
  canEditEvent: boolean;
  // Event handlers
  handleShowBrief: () => void;
  handlePrepareBrief: () => void;
  handleUpdateEvent: (title: string, startTime: string, endTime: string) => Promise<void>;
  handleCancelEvent: (message: string) => Promise<void>;
  handleReschedule?: () => void;
  rescheduleLoading?: boolean;
  showReschedule?: boolean;
}

const EventDetailPopoverContent = ({
  open, onClosePopover, popoverAnchorEl, event, briefLoading, shouldShowPrepareBrief, shouldShowShowBrief,
  canEditEvent, handleShowBrief, handlePrepareBrief, handleUpdateEvent, handleCancelEvent,
  handleReschedule, rescheduleLoading, showReschedule,
}: Props): ReactElement => {
  const [showEditModal, setShowEditModal] = useState(false);
  
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    onClosePopover();
  };
  
  const startTime = event.start;
  const endTime = event.end;
  const allDay = event.allDay;
  const title = event.title;
  const locationText = event.location;

  const timeText = !allDay && startTime && endTime ? formatSlotTimeTiny(
    DateTime.fromISO(startTime).toJSDate(),
    DateTime.fromISO(endTime).toJSDate(),
    getLocalTimeZoneName()
  ) : '';

  const selectedDate = startTime ? DateTime.fromISO(startTime).toISODate() || '' : '';
  const leaderInfo = event.leaderInfo || [];
  const calendarInfo = event.calendarInfo || [];
  const attendees = event.attendees;
  const busy = event.busy;
  const bufferStartMinutes = event.bufferStartMinutes;
  const bufferEndMinutes = event.bufferEndMinutes;
  const isRecurringEvent = !!event.recurringRootEventIdr;
  

  const calendarColor = calendarInfo ? calendarInfo[0]?.backgroundColor : '';
  const calendars = calendarInfo?.map(calendar => 
    calendar.additionalCalendarEmail ? calendar.additionalCalendarEmail.name : calendar.summary) || [];
  const noResponseCount = attendees?.filter(a => a.responseStatus === 'needsAction').length || 0;
  const tentativeCount = attendees?.filter(a => a.responseStatus === 'tentative').length || 0;
  const declinedCount = attendees?.filter(a => a.responseStatus === 'declined').length || 0;
  const acceptedCount = attendees?.filter(a => a.responseStatus === 'accepted').length || 0;

  const statusIcon = (attendee: EventAttendee) => {
    const status = attendee.responseStatus;

    if (status === 'accepted') {
      return 'accepted';
    } else if (status === 'declined') {
      return 'declined';
    } else if (status === 'tentative') {
      return 'tentative';
    } else if (status === 'needsAction' || status === 'none') {
      return 'needsAction';
    }
    return 'needsAction';
  };
  
  const renderStatusIcon = (attendee: EventAttendee) => {
    const status = statusIcon(attendee);
    
    if (status === 'accepted') {
      return <CabIcon Icon={IoCheckmarkCircle} sx={{color: colors.greenPrimary}} alt='Accepted'/>;
    } else if (status === 'declined') {
      return <CabIcon Icon={IoCloseCircle} sx={{color: colors.redError}} alt='Declined'/>;
    } else if (status === 'tentative') {
      return <CabIcon Icon={IoHelpCircle} sx={{color: colors.coralLight}} alt='Maybe'/>;
    } else if (status === 'needsAction') {
      return <CabIcon Icon={IoInformationCircle} sx={{color: colors.purplePrimary}} alt='Awaiting'/>;
    }
    return <CabIcon Icon={IoInformationCircle} sx={{color: colors.purplePrimary}} alt='Awaiting'/>;
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          padding: '4px',
          maxWidth: "500px"
        }}>
        <IconButton
          onClick={onClosePopover}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
          }}
        >
          <CabIcon Icon={IoClose} alt='Close' />
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            padding: 2
          }}>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start'
            }}>
            <Box sx={{
              marginRight: 2
            }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1
                }}>
                <CabIcon Icon={IoSquare} sx={{color: calendarColor, marginTop: '3px'}} 
                  alt={calendarInfo ? calendarInfo[0]?.summary : ''} />
                <Typography variant="h5">{title}</Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1
                }}>
                {isRecurringEvent ? (
                  <CabIcon Icon={IoRepeat} />
                ) : <Box sx={{
                  paddingLeft: 2
                }} />}
                {timeText ? (
                  <Typography variant="body2" color={colors.black800}>
                    {`${selectedDate} at ${timeText}`}
                  </Typography>
                ) : (
                  <Typography>{selectedDate}</Typography>
                )}
              </Box>

              {locationText && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1
                  }}>
                  <CabIcon Icon={IoLocationOutline} />
                  <Typography variant="body2" color={colors.black800}>
                    {locationText}
                  </Typography>
                </Box>
              )}

              {leaderInfo && leaderInfo.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    marginLeft: 3
                  }}>
                  <AvatarGroup max={10}>
                    {leaderInfo.map(leader => {
                      if (leader !== undefined) {
                        return (
                          <CabAvatar key={leader.id} name={`${leader.first_name} ${leader.last_name}`} 
                            src={leader.pic_url} color={leader.color} size='small' />
                        );
                      } else {
                        return null;
                      }
                    })}
                  </AvatarGroup>
                </Box>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start'
            }}>
            <CabIcon 
              Icon={IoCalendarClearOutline} 
              sx={{color: colors.black800, marginTop: '3px'}} 
              alt='Calendars' 
            />
            <Box>
              <Typography variant="body1" color={colors.black800}>{calendars.join(', ')}</Typography>
            </Box>
          </Box>
          {attendees && attendees?.length > 0 && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start'
                }}>
                <CabIcon Icon={IoPeopleOutline} sx={{color: colors.black800, marginTop: '3px'}} alt='Attendees' />
                <Box>
                  <Typography variant="body1" color={colors.black800}>
                    {`${attendees.length} guest(s)`}
                  </Typography>
                  {acceptedCount > 0 && (
                    <Typography variant="body2" color={colors.black800}>
                      {`${acceptedCount} yes`}
                    </Typography>
                  )}
                  {declinedCount > 0 && (
                    <Typography variant="body2" color={colors.black800}>
                      {`${declinedCount} no`}
                    </Typography>
                  )}
                  {tentativeCount > 0 && (
                    <Typography variant="body2" color={colors.black800}>
                      {`${tentativeCount} maybe`}
                    </Typography>
                  )}
                  {noResponseCount > 0 && (
                    <Typography variant="body2" color={colors.black800}>
                      {`${noResponseCount} awaiting`}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{
                marginTop: 1
              }}>
                {attendees.map((attendee: EventAttendee, i) => {
                  return (
                    <Box
                      key={(attendee.email || attendee.name) + i}
                      sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center'
                      }}>
                      {renderStatusIcon(attendee)}
                      <Typography>
                        {attendee.displayName !== '' ? attendee.displayName : attendee.email}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start'
            }}>
            <CabIcon Icon={IoBriefcaseOutline} sx={{color: colors.black800, marginTop: '3px'}} alt='Free Busy' />
            <Box>
              <Typography variant="body1" color={colors.black800}>
                {busy ? 'Busy' : 'Free'}
              </Typography>
            </Box>
          </Box>
          {(bufferStartMinutes || bufferEndMinutes) && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start'
                }}>
                <CabIcon
                  Icon={IoTimerOutline}
                  sx={{color: colors.black800, marginTop: '3px'}}
                  alt='Time Buffers'
                />
                <Box>
                  <Typography variant="body1" color={colors.black800}>
                    Buffer Time
                  </Typography>
                  {bufferStartMinutes && (
                    <Typography variant="body2" color={colors.black800}>
                      Before: {bufferStartMinutes} minutes
                    </Typography>
                  )}
                  {bufferStartMinutes && (
                    <Typography variant="body2" color={colors.black800}>
                      After: {bufferEndMinutes} minutes
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
          
          
          
          {(shouldShowPrepareBrief || shouldShowShowBrief || (startTime && endTime) || showReschedule) && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: 2,
                gap: 1,
                flexWrap: "wrap"
              }}>
              {showReschedule && handleReschedule && (
                <CabButton
                  buttonType="secondary"
                  onClick={handleReschedule}
                  disabled={!!rescheduleLoading}
                  icon={
                    rescheduleLoading ? (
                      <CabSpinner scale={1} />
                    ) : (
                      <CabIcon Icon={IoRefreshOutline} />
                    )
                  }
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Reschedule
                </CabButton>
              )}
              {startTime && endTime && canEditEvent && (
                <CabButton
                  buttonType="secondary"
                  onClick={() => setShowEditModal(true)}
                  icon={<CabIcon Icon={IoCreateOutline} />}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Edit Event
                </CabButton>
              )}
              {shouldShowPrepareBrief && (
                <CabButton
                  buttonType="secondary"
                  onClick={handlePrepareBrief}
                  disabled={briefLoading}
                  icon={
                    briefLoading ? (
                      <CabSpinner scale={1} />
                    ) : (
                      <CabIcon Icon={IoDocumentTextOutline} />
                    )
                  }
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Prepare Brief
                </CabButton>
              )}
              {shouldShowShowBrief && (
                <CabButton
                  buttonType="secondary"
                  onClick={handleShowBrief}
                  icon={<CabIcon Icon={IoDocumentTextOutline} />}
                >
                  Show Brief
                </CabButton>
              )}
            </Box>
          )}
        </Box>
          
      </Box>
      {startTime && endTime && (
        <EditEventModal
          open={showEditModal}
          onClose={handleCloseEditModal}
          eventTitle={title || ''}
          currentStartTime={startTime}
          currentEndTime={endTime}
          onUpdateEvent={handleUpdateEvent}
          onCancelEvent={handleCancelEvent}
        />
      )}
    </>
  );
};

export const EventDetailPopover = ({
  event, eventLoading, ...props
}: Omit<Props, 'event'> & { event?: UICalendarEventConsolidated }): ReactElement => {
  const content = !event && eventLoading ? <Box
    sx={{
      minWidth: '300px',
      minHeight: '300px',
      paddingTop: 4
    }}><CabSpinner /></Box>
    : !event ? <Typography>No event found</Typography>
      : <EventDetailPopoverContent {...props} event={event} eventLoading={eventLoading} />;

  return (
    <Popover
      open={props.open}
      onClose={props.onClosePopover}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center'
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      anchorEl={props.popoverAnchorEl as Element}
    >
      {content}
    </Popover>
  );
};

export default EventDetailPopover;
